import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Sparkles, Moon, Sun, Star, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function EarlyAccess() {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark-mode"));

  useEffect(() => {
    if (!user) return;
    if (!user.is_supporter) {
      navigate("/support", { replace: true });
    }
  }, [user, navigate]);

  const toggleDark = (v) => {
    setDark(v);
    if (v) {
      document.documentElement.classList.add("dark-mode");
      localStorage.setItem("lumen_dark", "1");
    } else {
      document.documentElement.classList.remove("dark-mode");
      localStorage.removeItem("lumen_dark");
    }
  };

  const T = (en, es) => (lang === "es" ? es : en);

  if (!user?.is_supporter) return null;

  return (
    <div>
      <div className="flex items-center gap-2 text-[var(--accent)]">
        <Star className="w-4 h-4 fill-[var(--accent)]" strokeWidth={1.5} />
        <span className="uppercase tracking-[0.3em] text-[10px]">
          {T("Supporter · Early access", "Sostenedor · Acceso anticipado")}
        </span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">
        {T("Try tomorrow, today.", "Prueba el mañana, hoy.")}
      </h1>
      <p className="mt-4 text-[var(--text-secondary)] max-w-2xl leading-relaxed">
        {T(
          "Thanks for keeping FaithCycle alive. Here's what's cooking — turn features on as they're ready.",
          "Gracias por mantener a FaithCycle viva. Esto es lo que está en el horno — actívalas cuando estén listas."
        )}
      </p>

      <div className="mt-10 max-w-2xl space-y-4">
        {/* Dark mode - AVAILABLE */}
        <div
          data-testid="feature-dark-mode"
          className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--surface-elevated)] p-6 flex items-center gap-5"
        >
          <span className="w-12 h-12 rounded-full bg-[var(--highlight)]/50 flex items-center justify-center flex-shrink-0">
            {dark ? <Moon className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} /> : <Sun className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />}
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-xl tracking-tight">{T("Nighttime reading", "Lectura nocturna")}</h3>
              <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--accent)] text-white">
                {T("Live", "Activo")}
              </span>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {T(
                "A warm, low-contrast dark theme for late-evening devotions.",
                "Un tema oscuro cálido y de bajo contraste para devociones nocturnas."
              )}
            </p>
          </div>
          <Switch
            data-testid="dark-mode-toggle"
            checked={dark}
            onCheckedChange={toggleDark}
            className="data-[state=checked]:bg-[var(--accent)]"
          />
        </div>

        {/* Compare translations - AVAILABLE */}
        <div
          data-testid="feature-compare"
          className="rounded-2xl border border-[var(--accent)]/40 bg-[var(--surface-elevated)] p-6 flex items-center gap-5"
        >
          <span className="w-12 h-12 rounded-full bg-[var(--highlight)]/50 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-[var(--accent)]" strokeWidth={1.5} />
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-xl tracking-tight">{T("Compare translations", "Comparar traducciones")}</h3>
              <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--accent)] text-white">
                {T("Live", "Activo")}
              </span>
            </div>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              {T(
                "Open two versions side-by-side in the reader — KJV, WEB, RV1960, RVG or NTV.",
                "Abre dos versiones lado a lado en el lector — KJV, WEB, RV1960, RVG o NTV."
              )}
            </p>
          </div>
          <a
            href="/read"
            className="text-xs uppercase tracking-widest text-[var(--accent)] hover:underline"
          >
            {T("Open", "Abrir")}
          </a>
        </div>

        {/* Coming soon items */}
        {[
          {
            title: T("Verse cards", "Tarjetas de versículos"),
            desc: T("Turn any verse + reflection into a shareable image.", "Convierte cualquier versículo + reflexión en una imagen para compartir."),
          },
          {
            title: T("Verse highlights", "Resaltar versículos"),
            desc: T("Personal color highlights and private notes on any verse.", "Resaltados con colores y notas privadas en cualquier versículo."),
          },
          {
            title: T("Audio Bible", "Biblia en audio"),
            desc: T("Listen to any chapter with a natural narrator voice.", "Escucha cualquier capítulo con una voz de narrador natural."),
          },
        ].map((f, i) => (
          <div
            key={i}
            data-testid={`upcoming-feature-${i}`}
            className="rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6 flex items-center gap-5"
          >
            <span className="w-12 h-12 rounded-full bg-[var(--surface)] flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-[var(--text-secondary)]" strokeWidth={1.5} />
            </span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-xl tracking-tight">{f.title}</h3>
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--border)] text-[var(--text-secondary)]">
                  {T("Coming", "Próximo")}
                </span>
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-1">{f.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
